import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";

const TIP_PAD = 8;
const TIP_GAP = 8;

type TipPos = { left: number; top: number };

/**
 * A question-mark that reveals an explanation on hover or click.
 *
 * Screens fill up with paragraphs of guidance that are essential the first
 * time and noise every time after, so the long form lives behind this and the
 * label next to it stays short.
 *
 * The bubble is portalled and positioned in viewport coordinates: hints sit
 * inside cards and dialogs that clip their overflow (and `.bf-card` applies a
 * backdrop-filter, which makes any `fixed` child resolve against the card
 * rather than the viewport), so rendering in place would cut it off. After
 * measuring, the bubble flips above/below the mark and shifts horizontally so
 * it stays inside the window.
 */
export function InfoHint({
  children,
  label = "More information",
  className = "",
  side = "top",
}: {
  children: React.ReactNode;
  label?: string;
  className?: string;
  side?: "top" | "bottom";
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TipPos | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const id = useId();

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const btn = btnRef.current?.getBoundingClientRect();
      const tip = tipRef.current;
      if (!btn || !tip) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const tipW = tip.offsetWidth;
      const tipH = tip.offsetHeight;
      const spaceAbove = btn.top - TIP_PAD;
      const spaceBelow = vh - btn.bottom - TIP_PAD;
      let above = side === "top";
      if (above && spaceAbove < tipH + TIP_GAP && spaceBelow > spaceAbove) {
        above = false;
      } else if (!above && spaceBelow < tipH + TIP_GAP && spaceAbove > spaceBelow) {
        above = true;
      }
      let top = above ? btn.top - TIP_GAP - tipH : btn.bottom + TIP_GAP;
      top = Math.min(Math.max(top, TIP_PAD), Math.max(TIP_PAD, vh - tipH - TIP_PAD));
      let left = btn.left + btn.width / 2 - tipW / 2;
      left = Math.min(Math.max(left, TIP_PAD), Math.max(TIP_PAD, vw - tipW - TIP_PAD));
      setPos((prev) =>
        prev && prev.left === left && prev.top === top ? prev : { left, top },
      );
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, side]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={
          "inline-flex shrink-0 items-center justify-center rounded-full text-bf-muted/70 transition hover:text-bf-text focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50 " +
          className
        }
      >
        <HelpCircle size={14} aria-hidden />
      </button>
      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            className="pointer-events-none fixed z-[120] w-64 rounded-lg border border-white/10 bg-bf-surface px-3 py-2 text-xs leading-relaxed text-bf-muted shadow-xl shadow-black/50"
            style={{
              left: pos?.left ?? 0,
              top: pos?.top ?? 0,
              visibility: pos ? "visible" : "hidden",
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * A field label with an optional hint beside it. Keeps the label typography
 * identical everywhere instead of each form inventing its own.
 */
export function FieldLabel({
  children,
  hint,
  htmlFor,
  className = "",
}: {
  children: React.ReactNode;
  hint?: React.ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <span className={"mb-1 flex items-center gap-1.5 " + className}>
      <label htmlFor={htmlFor} className="text-xs font-medium text-bf-muted">
        {children}
      </label>
      {hint && <InfoHint>{hint}</InfoHint>}
    </span>
  );
}
